import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";

/**
 * Global 401 handler — bridges the TanStack QueryCache to react-router.
 *
 * Audit 260512-nmw H-9. RequireAuth only bounces on mount; a mid-session
 * cookie expiry leaves the user on a broken page with cached data until
 * they happen to navigate. This subscriber listens to every cached-query
 * error and, on 401, clears the session cache and redirects to /login
 * with the current URL preserved in `?next=`.
 *
 * Mounted once inside BrowserRouter in main.tsx — renders nothing.
 */
export function GlobalErrorBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // v5 emits a notify event whose action carries the error payload.
      if (event.type !== "updated") return;
      const action = event.action as { type?: string; error?: unknown };
      if (action.type !== "error") return;

      const error = action.error;
      if (!(error instanceof ApiError) || error.status !== 401) return;

      // Skip the `me` query — RequireAuth owns its own bounce. Avoids a
      // double-navigate on protected routes that already redirect.
      const key = event.query.queryKey?.[0];
      if (key === "me") return;

      // Don't redirect from auth surfaces — they're already where the
      // user should land.
      if (
        location.pathname.startsWith("/login") ||
        location.pathname.startsWith("/auth/")
      ) {
        return;
      }

      // Clear the session cache so the next paint after the redirect
      // doesn't flash prior-user data (mirrors useLogout cache-clear).
      queryClient.setQueryData(["me"], null);
      queryClient.invalidateQueries({ queryKey: ["me"] });

      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    });
    return unsubscribe;
  }, [navigate, location.pathname, location.search, queryClient]);

  return null;
}
